import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, Star, MessageCircle } from 'lucide-react';
import UpgradeModal from './UpgradeModal';

const QueuePage = () => {
  const navigate = useNavigate();
  const [myQueue, setMyQueue] = useState([]); // People waiting for me
  const [myRequests, setMyRequests] = useState([]); // Where I'm waiting
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('waiting-for-me'); // 'waiting-for-me' or 'im-waiting'
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    fetchQueueData();
    // Refresh every 10 seconds
    const interval = setInterval(fetchQueueData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchQueueData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch people waiting for me
      const queueResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/queue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (queueResponse.ok) {
        const queueData = await queueResponse.json();
        setMyQueue(queueData.queue || []);
      }
      
      // Fetch my requests (where I'm waiting)
      const myRequestsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/queue/my-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (myRequestsResponse.ok) {
        const requestsData = await myRequestsResponse.json();
        setMyRequests(requestsData.requests || []);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching queue data:', error);
      setLoading(false);
    }
  };

  const startSpeedDate = async (person) => {
    try {
      const token = localStorage.getItem('token');
      
      // First check availability with the backend
      const checkResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          match_id: person.match_id
        })
      });
      
      const result = await checkResponse.json();
      
      // Check for email verification required error
      if (checkResponse.status === 403 && result.detail?.action === 'verify_email') {
        alert('📧 Email Verification Required\n\nPlease verify your email to start speed dating. Check your inbox for the confirmation link.');
        return;
      }
      
      if (!checkResponse.ok) {
        alert(result.detail?.message || 'Failed to start speed date. Please try again.');
        return;
      }
      
      // Check for various blocking conditions
      if (result.requester_limit_reached) {
        setShowUpgradeModal(true);
        return;
      }
      
      if (result.target_limit_reached) {
        alert(`${person.requester_name} has used all their free speed dating sessions this month. They need to upgrade to Premium.`);
        return;
      }
      
      if (result.queued) {
        alert(`${person.requester_name} is currently in another date. They'll need to finish first before you can connect.`);
        return;
      }
      
      // All checks passed - DON'T remove from queue yet
      // Queue will be removed when the queued user (requester) joins
      // This keeps their "I'm waiting for" notification visible
      
      console.log('✅ Target accepting queued user - keeping queue entry for requester notification');
      
      // Navigate to speed date with partner info
      navigate(`/speed-dating-session`, {
        state: { 
          partnerInfo: {
            id: person.requester_id,
            name: person.requester_name,
            photo_url: null
          },
          skipQueueCheck: true,  // Already checked
          queueId: person.queue_id  // Pass queue_id to remove after requester joins
        }
      });
    } catch (error) {
      console.error('Error starting speed date:', error);
      alert('Failed to start speed date. Please try again.');
    }
  };

  const cancelRequest = async (queueId, targetName) => {
    // Ask for confirmation first
    const confirmed = window.confirm(
      `Are you sure you want to cancel your speed date request with ${targetName}?\n\n` +
      `They'll be notified that you're no longer available. You can always request again later!`
    );
    
    if (!confirmed) {
      return; // User clicked "Cancel" on the confirmation
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/queue/${queueId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel request');
      }
      
      const result = await response.json();
      console.log('✅ Cancel successful:', result);
      
      // Show success message
      alert(`Request cancelled successfully!\n\n${targetName} has been notified. Keep exploring other amazing matches! 💕`);
      
      // Refresh data immediately
      await fetchQueueData();
    } catch (error) {
      console.error('Error canceling request:', error);
      alert(`Failed to cancel: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex items-center justify-center overflow-x-hidden">
        <div className="text-white text-xl">Loading queue...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Speed Date Queue</h1>
          <p className="text-white/70">Manage your waiting list and see where you're queued</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 bg-black/20 rounded-xl p-2">
          <button
            onClick={() => setActiveTab('waiting-for-me')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition ${
              activeTab === 'waiting-for-me'
                ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Users className="w-5 h-5" />
              <span>Waiting for Me ({myQueue.length})</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('im-waiting')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition ${
              activeTab === 'im-waiting'
                ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>I'm Waiting ({myRequests.length})</span>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {activeTab === 'waiting-for-me' && (
            <>
              {myQueue.length === 0 ? (
                <div className="bg-white/5 backdrop-blur-md rounded-2xl p-12 text-center border border-white/10">
                  <Users className="w-16 h-16 text-white/40 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No One Waiting</h3>
                  <p className="text-white/60">When someone wants to speed date with you while you're busy, they'll appear here.</p>
                </div>
              ) : (
                myQueue.map((person, index) => (
                  <div key={person.queue_id} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-rose-400 transition">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Avatar */}
                        <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                          {person.requester_name.charAt(0)}
                        </div>
                        
                        {/* Info */}
                        <div>
                          <h3 className="text-xl font-bold text-white">{person.requester_name}</h3>
                          <p className="text-white/70">{person.requester_age} years old</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-semibold">
                              Position #{index + 1}
                            </span>
                            {person.requester_tier === 'vip' && (
                              <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs font-semibold flex items-center space-x-1">
                                <Star className="w-3 h-3" />
                                <span>VIP</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <button
                        onClick={() => startSpeedDate(person)}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-3 px-6 rounded-xl transition shadow-lg"
                      >
                        Start Date Now
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'im-waiting' && (
            <>
              {myRequests.length === 0 ? (
                <div className="bg-white/5 backdrop-blur-md rounded-2xl p-12 text-center border border-white/10">
                  <Clock className="w-16 h-16 text-white/40 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Not Waiting Anywhere</h3>
                  <p className="text-white/60">When you try to speed date with someone who's busy, you'll see your queue position here.</p>
                </div>
              ) : (
                myRequests.map((request) => (
                  <div key={request.queue_id} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Avatar */}
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                          {request.target_name.charAt(0)}
                        </div>
                        
                        {/* Info */}
                        <div>
                          <h3 className="text-xl font-bold text-white">{request.target_name}</h3>
                          <p className="text-white/70">Waiting since {new Date(request.created_at).toLocaleTimeString()}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs font-semibold">
                              Position #{request.position}
                            </span>
                            <span className="text-white/60 text-sm">
                              {request.status === 'pending' ? '⏳ Waiting...' : '✅ Notified'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                        {/* Join Speed Date Button - Only show if user is #1 in queue or VIP */}
                        {(request.position === 1 || request.status === 'notified') && (
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                
                                // Check if target is still available via queue check
                                const checkResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/request`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: JSON.stringify({
                                    match_id: request.match_id
                                  })
                                });
                                
                                if (checkResponse.ok) {
                                  const result = await checkResponse.json();
                                  
                                  if (result.requester_limit_reached) {
                                    setShowUpgradeModal(true);
                                  } else if (result.target_limit_reached) {
                                    alert(`${result.target_name} has used all their free sessions. They need to upgrade.`);
                                  } else if (result.queued) {
                                    alert(`${request.target_name} is still busy. You're still #${result.position} in queue.`);
                                  } else {
                                    // Available! Remove from queue and start date
                                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/queue/remove`, {
                                      method: 'POST',
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      },
                                      body: JSON.stringify({
                                        queue_id: request.queue_id
                                      })
                                    });
                                    
                                    navigate(`/speed-dating-session`, {
                                      state: { 
                                        partnerInfo: {
                                          id: request.target_id,
                                          name: request.target_name,
                                          photo_url: null
                                        },
                                        skipQueueCheck: true
                                      }
                                    });
                                  }
                                }
                              } catch (error) {
                                console.error('Error joining speed date:', error);
                                alert('Failed to join speed date. Please try again.');
                              }
                            }}
                            className="w-full sm:w-auto bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-3 px-6 rounded-xl transition shadow-lg"
                          >
                            Join Speed Date
                          </button>
                        )}
                        
                        <button
                          onClick={() => cancelRequest(request.queue_id, request.target_name)}
                          className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-xl transition"
                        >
                          Cancel Request
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="session_complete"
        usageStats={{ used: 2, limit: 2 }}
      />
    </div>
  );
};

export default QueuePage;
